import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import BidFactoryWizard from '../components/BidFactoryWizard';
import './ResponseBuilder.css';

const ResponseBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [tender, setTender] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTenderDetail();
  }, [id]);

  const fetchTenderDetail = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`/api/tenders/${id}`);
      setTender(response.data);
    } catch (err) {
      console.error('Error fetching tender:', err);
      // Try to use mock data as fallback
      const mockTender = getMockTenderDetail(id);
      if (mockTender) {
        setTender(mockTender);
      } else {
        setError('Failed to load tender details. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleWizardComplete = (responseData) => {
    // Handle completed wizard - navigate back to tender details
    console.log('Wizard completed with response:', responseData);

    // Preserve navigation state when completing
    if (location.state?.from) {
      navigate(`/tender/${id}`, {
        state: {
          ...location.state,
          responseSubmitted: true
        }
      });
    } else {
      navigate(`/tender/${id}`, {
        state: { responseSubmitted: true }
      });
    }
  };

  const handleWizardCancel = () => {
    // Navigate back to tender details or previous location
    if (location.state?.from) {
      navigate(`/tender/${id}`, { state: location.state });
    } else {
      navigate(`/tender/${id}`);
    }
  };

  if (loading) {
    return (
      <div className="response-builder-loading">
        <div className="loading-spinner"></div>
        <p>Loading tender details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="response-builder-error">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={fetchTenderDetail} className="btn btn-primary">
          Try Again
        </button>
        <button onClick={handleWizardCancel} className="btn btn-secondary">
          Go Back
        </button>
      </div>
    );
  }

  if (!tender) {
    return (
      <div className="response-builder-error">
        <h2>Tender Not Found</h2>
        <p>The requested tender could not be found.</p>
        <button onClick={() => navigate('/my-tenders')} className="btn btn-primary">
          View Saved Tenders
        </button>
      </div>
    );
  }

  return (
    <BidFactoryWizard
      tender={tender}
      onComplete={handleWizardComplete}
      onCancel={handleWizardCancel}
    />
  );
};

// Mock data function for development/testing
const getMockTenderDetail = (id) => ({
  tender_id: id,
  id: id,
  title: "IT Infrastructure Modernization Services",
  description: "The Government of Canada is seeking qualified vendors to provide IT infrastructure modernization services including cloud migration, security improvements, and system integration.",
  organization: {
    name: "Public Services and Procurement Canada",
    contracting_entity: "Public Services and Procurement Canada"
  },
  value: { amount: 2500000 },
  amount: 2500000,
  closing_date: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString(),
  publication_date: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
  solicitation_number: `SOL-${id}`,
  procurement_category: "Information Technology",
  'masterCategory-catégoriePrincipale': "Information Technology",
  'canadianMade-fabriquéCanadien': "yes",
  'experienceRequired-expérienceRequise': true,
  'indigenousBusinessRequired-entrepriseAutochtooneRequise': false,
  gsin_codes: ["D301", "D302", "D303"],
  delivery_regions: ["Ontario", "Quebec", "British Columbia"],
  'deliveryRegions-régionsLivraison': ["Ontario", "Quebec", "British Columbia"]
});

export default ResponseBuilder;
